import clsx from 'clsx';
import { usePageContext } from 'processes/Page/PageContext';
import { useMemo } from 'react';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { Col, Row, Container, Space, Typography, getTypographyDefaultColor } from '@ag1/ui';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { MessageAndCTAV2 as MessageAndCTAV2Props, SanityKeyed } from 'types/studio/studio';
import UnifiedButton from '../UnifiedButton/UnifiedButton';
import { UnifiedButtonType } from '../UnifiedButton/UnifiedButton.types';
import styles from './MessageAndCTAV2.module.scss';
import { CommonSectionProps } from '../types';

const MessageAndCTAV2 = (props: SanityKeyed<MessageAndCTAV2Props> & CommonSectionProps) => {
  const {
    _key: key,
    _type: sectionType,
    title,
    text,
    superHeadline,
    unifiedButton,
    bgColor,
    padding,
    theme
  } = props;

  const { shouldDisableUnifiedButton } = usePageContext();

  const disableUnifiedButton = useMemo(
    () => shouldDisableUnifiedButton(unifiedButton),
    [shouldDisableUnifiedButton, unifiedButton]
  );

  return (
    <div
      className={clsx(bgColor === 'stone' ? styles.stone : '', getThemeCSSClass(theme))}
      id={key}>
      <Container narrow>
        <Row className={styles.row}>
          <Col span={{ xxs: 12, l: 8 }}>
            <Space
              className={clsx(styles.space, getSectionPaddingClasses(styles, padding))}
              top={{ xxs: 40, s: 60, m: 80, l: 100 }}
              bottom={{ xxs: 40, s: 60, m: 80, l: 100 }}>
              {superHeadline && (
                <Typography
                  component="div"
                  variant="subtitle"
                  size="1"
                  noPadding
                  weight="700"
                  color={getTypographyDefaultColor(theme)}
                  className={styles.superHeadline}>
                  <CustomPortableText value={superHeadline} />
                </Typography>
              )}
              {title && (
                <Typography
                  component="div"
                  variant="headlineSans"
                  size="2"
                  noPadding
                  weight="400"
                  color={getTypographyDefaultColor(theme)}>
                  <CustomPortableText value={title} />
                </Typography>
              )}
              {text && (
                <Typography
                  component="div"
                  variant="subtitle"
                  size="2"
                  noPadding
                  weight="400"
                  color={getTypographyDefaultColor(theme)}
                  className={styles.text}>
                  <CustomPortableText value={text} />
                </Typography>
              )}
              {unifiedButton?.switchButton !== 'noButton' && (
                <div className={styles.buttonWrap}>
                  <UnifiedButton
                    unifiedButton={unifiedButton as UnifiedButtonType}
                    sectionType={sectionType}
                    size="medium"
                    className={styles.button}
                    theme={theme}
                    isDisabled={disableUnifiedButton}
                  />
                </div>
              )}
            </Space>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MessageAndCTAV2;
